/* A form over what the document actually has. ITEM 17 OF PLAN.md.
 *
 * Not a form per block kind. There are nineteen kinds and each has its own
 * fields; written out by hand that is nineteen forms to keep in step with
 * lib/types.ts, and the first kind added would leave one of them behind
 * silently — the same drift the schema went through for a year. So the form is
 * derived from the document in front of it: every leaf gets the control its
 * value deserves, and a kind nobody has written yet is editable the day it
 * exists.
 *
 * What that costs is field names. The document's own vocabulary is `body`,
 * `lead`, `kit`, `goal`, and inventing a second Czech one would mean two
 * vocabularies to learn instead of one. So a known name is glossed and the
 * path is shown beside it in full: an author reading `Tělo · blocks[3].body`
 * can find the same place in the JSON, which is the mode the other half of
 * the editor is.
 *
 * The preview renders through blocks/inline.tsx, the same code that draws the
 * material for a reader. That is the whole of why there is no raw HTML in it:
 * React escapes what it renders, and a link is refused unless it points inside
 * the portal. The author is signed in and the rule does not soften for them —
 * the portal has one boundary and no reason to open a hole in it for comfort.
 */

import { createContext, useContext, useState, type ReactNode } from "react";
import { paragraphs } from "../../blocks/inline";
import { useI18n } from "../../lib/i18n";

/* Fields that carry markdown, and therefore a preview. The block model names
   them: body, lead, d, a note and a table cell take `**bold**`, `` `code` ``
   and [label](/route). A prompt does not — it is text somebody pastes into a
   chat window, where an asterisk is an asterisk. */
const PROSE = new Set(["body", "lead", "d", "caption", "brief", "answer", "note", "rows", "head"]);

/* Long enough to want a box rather than a line. A claim is one sentence and a
   body is a paragraph; the difference is worth more than the consistency. */
const TALL = new Set(["body", "lead", "d", "brief", "answer", "prompt", "out", "core",
  "claim", "who", "can", "proof", "script", "note"]);

const GLOSS: Record<string, string> = {
  id: "Identifikátor", type: "Typ", title: "Titulek", eyebrow: "Nadtitulek",
  claim: "Věta pod titulkem", lead: "Uvození", body: "Tělo", note: "Poznámky",
  blocks: "Bloky", parts: "Části", k: "Druh bloku", goal: "Cíl", levels: "Úrovně",
  kit: "Sada", kind: "Druh materiálu", who: "Pro koho", can: "Co umí",
  proof: "Jak se pozná", prompt: "Zadání", core: "Základ zadání", out: "Ukázka výstupu",
  ready: "Hotový materiál", as: "Nosič", script: "Skript nahrávky", app: "Aplikace",
  minutes: "Minut", print: "Na papír", absent: "Proč chybí", meta: "Zařazení",
  subject: "Předmět", strand: "Stupeň", year: "Ročník", grade: "Ročník slovem",
  schoolType: "Typ školy", points: "Odrážky", steps: "Kroky", items: "Položky",
  head: "Hlavička tabulky", rows: "Řádky tabulky", fig: "Kresba", caption: "Popisek",
  q: "Otázka", answer: "Odpověď", brief: "Zadání cvičení", done: "Hotovo, když",
  layout: "Rozvržení", cs: "Česky", en: "Anglicky",
};

/* Running the prompt an item carries is the one thing this form knows about
   the material rather than about JSON. It is a deliberate exception: the button
   has to stand at the item it fills, and a form that renders every leaf the
   same way has nowhere else to put it. Everything else here stays generic. */
const Fill = createContext<((kind: string) => void) | null>(null);

/* How a field leaves the page. Leaving a box is finishing an edit, so blur is
   when the one field saves itself — the whole document is not sent anywhere,
   which is the point of step 6 of docs/plan/v3/00-datovy-model.md. A control
   with no blur worth waiting for — a checkbox, a number — saves on change. */
const Save = createContext<((path: (string | number)[], value: unknown) => void) | null>(null);

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** `{ cs, en }` and nothing else — the material's localized string. */
const isLocalized = (v: unknown): v is { cs?: string; en?: string } =>
  isObject(v) && Object.keys(v).length > 0
  && Object.keys(v).every((k) => k === "cs" || k === "en")
  && Object.values(v).every((x) => typeof x === "string");

/** The same document with one leaf replaced. Nothing is mutated: the editor
 *  compares the text it holds with the text it saved, and a value changed in
 *  place would make those two the same object and the change invisible. */
export function setAt(root: unknown, path: (string | number)[], value: unknown): unknown {
  if (!path.length) return value;
  const [head, ...rest] = path;
  if (typeof head === "number") {
    const list = Array.isArray(root) ? [...root] : [];
    list[head] = setAt(list[head], rest, value);
    return list;
  }
  const obj = isObject(root) ? { ...root } : {};
  obj[head as string] = setAt(obj[head as string], rest, value);
  return obj;
}

const label = (key: string | number): string =>
  typeof key === "number" ? `${key + 1}.` : GLOSS[key] ?? key;

/** The path as the server's edit route names it: `kit[2].title.cs`. */
export const asPath = (path: (string | number)[]): string =>
  path.map((p) => (typeof p === "number" ? `[${p}]` : `.${p}`)).join("").replace(/^\./, "");

/** The subtree at a path, or undefined where the document has none. */
export function getAt(root: unknown, path: (string | number)[]): unknown {
  let at: unknown = root;
  for (const step of path) {
    if (at === null || typeof at !== "object") return undefined;
    at = (at as Record<string | number, unknown>)[step];
  }
  return at;
}

function Preview({ text }: { text: string }) {
  const { href } = useI18n();
  if (!text.trim()) return null;
  return <div className="fld-preview">{paragraphs(text, href)}</div>;
}

function Line({ path, value, prose, name, onSet }: {
  path: (string | number)[]; value: string; prose: boolean; name?: string;
  onSet: (v: unknown) => void;
}) {
  const save = useContext(Save);
  const key = path[path.length - 1];
  const tall = typeof key === "string" ? TALL.has(key) : value.length > 60;
  return (
    <div className={"fld" + (prose ? " is-prose" : "")}>
      <label className="fld-label">
        {name ?? label(key ?? "")} <code className="fld-path">{asPath(path)}</code>
      </label>
      <div className="fld-pair">
        {tall
          ? <textarea className="fld-in" rows={Math.min(12, value.split("\n").length + 2)}
                      value={value} onChange={(e) => onSet(e.target.value)}
                      onBlur={() => save?.(path, value)} />
          : <input className="fld-in" type="text" value={value}
                   onChange={(e) => onSet(e.target.value)}
                   onBlur={() => save?.(path, value)} />}
        {prose ? <Preview text={value} /> : null}
      </div>
    </div>
  );
}

function Localized({ path, value, prose, onSet }: {
  path: (string | number)[]; value: { cs?: string; en?: string };
  prose: boolean; onSet: (v: unknown) => void;
}) {
  /* The field's own name, not the language's. A column of "Česky / Anglicky"
     says which half of a pair a box is and nothing about which field, and the
     author would be reading the path to find out — which is the JSON mode with
     extra steps. */
  const mine = label(path[path.length - 1] ?? "");
  return (
    <>
      <Line path={[...path, "cs"]} value={value.cs ?? ""} prose={prose}
            name={`${mine} · česky`}
            onSet={(v) => onSet({ ...value, cs: v as string })} />
      {value.en !== undefined ? (
        <Line path={[...path, "en"]} value={value.en} prose={prose}
              name={`${mine} · anglicky`}
              onSet={(v) => onSet({ ...value, en: v as string })} />
      ) : null}
    </>
  );
}

/** One value, whatever it turns out to be. */
function Node({ path, value, onSet }: {
  path: (string | number)[]; value: unknown; onSet: (v: unknown) => void;
}): ReactNode {
  const save = useContext(Save);
  const key = path[path.length - 1];
  /* An item of a list of notes is prose because the list is; a field is prose
     because it is named one. Both are asked here so that note[0] and body get
     the same preview. */
  const prose = typeof key === "string"
    ? PROSE.has(key)
    : PROSE.has(String(path[path.length - 2] ?? ""));

  if (typeof value === "string") {
    return <Line path={path} value={value} prose={prose} onSet={onSet} />;
  }
  if (typeof value === "number") {
    return (
      <div className="fld">
        <label className="fld-label">
          {label(key ?? "")} <code className="fld-path">{asPath(path)}</code>
        </label>
        <input className="fld-in fld-num" type="number" value={value}
               onChange={(e) => onSet(e.target.valueAsNumber)}
               onBlur={(e) => {
                 const n = e.target.valueAsNumber;
                 if (!Number.isNaN(n)) save?.(path, n);
               }} />
      </div>
    );
  }
  if (typeof value === "boolean") {
    return (
      <div className="fld fld-flag">
        <label className="fld-label">
          <input type="checkbox" checked={value} onChange={(e) => {
            onSet(e.target.checked);
            save?.(path, e.target.checked);
          }} />
          {" "}{label(key ?? "")} <code className="fld-path">{asPath(path)}</code>
        </label>
      </div>
    );
  }
  if (isLocalized(value)) {
    return <Localized path={path} value={value} prose={prose} onSet={onSet} />;
  }
  if (Array.isArray(value)) {
    return (
      <Group path={path} count={value.length}>
        {value.map((item, i) => (
          <Node key={i} path={[...path, i]} value={item}
                onSet={(v) => onSet(setAt(value, [i], v))} />
        ))}
      </Group>
    );
  }
  if (isObject(value)) {
    /* An item of the kit, which is the one shape with an action of its own.
       Without a prompt there is nothing to run and the button is not drawn —
       saying so with a disabled control would be saying it in the wrong place. */
    const kind = path[0] === "kit" && typeof value["kind"] === "string"
      && value["prompt"] !== undefined ? String(value["kind"]) : undefined;
    return (
      <Group path={path} count={Object.keys(value).length} fill={kind}
             note={typeof value["k"] === "string" ? String(value["k"])
               : typeof value["kind"] === "string" ? String(value["kind"]) : undefined}>
        {Object.entries(value).map(([k, v]) => (
          <Node key={k} path={[...path, k]} value={v}
                onSet={(next) => onSet(setAt(value, [k], next))} />
        ))}
      </Group>
    );
  }
  return null;
}

/* Collapsed below the top level. A lesson is a goal, ten materials and every
   block of each of them; opened whole it is several hundred fields and the one
   being looked for is somewhere in the middle. */
function Group({ path, count, note, fill, children }: {
  path: (string | number)[]; count: number; note?: string; fill?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(path.length <= 1);
  const onFill = useContext(Fill);
  const key = path[path.length - 1];
  return (
    <section className="fld-group">
      <button type="button" className="fld-twist plain" aria-expanded={open}
              onClick={() => setOpen(!open)}>
        <span className="ed-caret">{open ? "▾" : "▸"}</span>
        {label(key ?? "")}
        {note ? <em className="fld-kind">{note}</em> : null}
        <span className="ed-count">{count}</span>
        <code className="fld-path">{asPath(path)}</code>
        {fill && onFill ? (
          <span className="fld-fill" role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onFill(fill); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onFill(fill); }
                }}>
            Doplnit modelem
          </span>
        ) : null}
      </button>
      {open ? <div className="fld-kids">{children}</div> : null}
    </section>
  );
}

export function Fields({ doc, onChange, onFill, onSave }: {
  doc: Record<string, unknown>; onChange: (next: unknown) => void;
  onFill?: (kind: string) => void;
  /** One field leaving the page saves itself; without this the form is only a
   *  view over the buffer, which is what the JSON mode already is. */
  onSave?: (path: (string | number)[], value: unknown) => void;
}) {
  return (
    <Fill.Provider value={onFill ?? null}>
    <Save.Provider value={onSave ?? null}>
    <div className="fld-form">
      {Object.entries(doc).map(([k, v]) => (
        <Node key={k} path={[k]} value={v} onSet={(next) => onChange(setAt(doc, [k], next))} />
      ))}
    </div>
    </Save.Provider>
    </Fill.Provider>
  );
}
